import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "mb-3 flex items-center justify-between" }
const _hoisted_2 = { class: "flex items-center" }
const _hoisted_3 = {
  class: "color-gray-80 text-base font-semibold",
  "data-testid": "date-condition-group"
}
const _hoisted_4 = {
  key: 0,
  class: "text-red-60 mt-2.5 text-xs font-medium"
}
const _hoisted_5 = { class: "flex items-center" }
const _hoisted_6 = { class: "color-gray-80 text-base font-semibold" }
const _hoisted_7 = { class: "flex min-h-8 items-center gap-4" }
const _hoisted_8 = { class: "text-gray-80 text-sm font-medium" }
const _hoisted_9 = { class: "mb-3 flex items-center" }
const _hoisted_10 = { class: "color-gray-80 text-base font-semibold" }

import { computed, watch } from 'vue'
import { Tippy } from 'vue-tippy'
import { cloneDeep, debounce, isEqual } from 'lodash-es'

import { UiPillToggle, UiTag } from '@klausapp/ui-kit'
import { InfoIcon } from '@klausapp/ui-kit/icons'
import i18n from '@/i18n'
import {
  type DropdownSelectedValue,
  type AddedFilter,
  type FilterItemEmit,
  type FilterOption,
  type ValueComponentProps,
  FilterValueType,
  type FilterModalType,
} from '@/components/filters/types'

import FilterGroup from '@/components/filters/FilterGroup.vue'
import SearchInput from '@/components/SearchInput.vue'
import { isActiveFilter } from '@/components/filters/utils'

import { translationExists } from '@/i18n/utils'
import { type DeconstructedOption } from '../../utils/filters'
import { type DefaultFilterOption } from '../../api/filters'
import useFilterOptions from '../../composables/useFilterOptions'
import useVisibleFilters from '../../composables/useVisibleFilters'
import useFilterValues from '../../composables/useFilterValues'

import HelpdeskFilterGroup from './HelpdeskFilterGroup.vue'

import {
  type GroupFilter,
  getConversationFilters,
  dateGroupFilter,
  helpdeskGroupFilter,
  getAllGroupsFilter,
  hasIntelligenceIcon,
  hasBotIcon,
} from './filterGrouping'

import { addLabel, getDescriptionKey, getDescriptionOptions, isDowngradedFilter, forcedFilterOption } from './utils'


export default /*@__PURE__*/_defineComponent({
  __name: 'FilterOptions',
  props: {
    options: {},
    autoqaFields: {},
    spotlightFields: {},
    customCategoryFields: {},
    intelligentFilterFields: {},
    modelValue: {},
    editModal: { type: Boolean },
    isModal: { type: Boolean },
    reviewerQa: { type: Boolean },
    hideAllConversationFilters: { type: Boolean },
    modalType: {}
  },
  emits: ["update:modelValue", "disable-selecting-tickets"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const { groupsWithToggle } = getConversationFilters(
  !!props.reviewerQa,
  !props.hideAllConversationFilters && !props.editModal,
)

const translateLabels = (values: DeconstructedOption[]) => {
  return values.map((value) => ({
    ...value,
    option: addLabel(value.option),
    condition: addLabel(value.condition),
  }))
}

const {
  visibleFilters,
  validVisibleFilters,
  selectedOptionNames,
  removeFilter,
  setFilterCondition,
  getFilterById,
  setFilterValue,
  replaceOrAddFilter,
  getLastFilter,
  addEmptyFilter,
  setSelectedOptions,
  getFilteredItems,
} = useVisibleFilters()

const { filterOptions, getFilterConditions } = useFilterOptions(
  props.options,
  props.autoqaFields,
  props.spotlightFields,
  props.customCategoryFields,
  props.intelligentFilterFields,
  selectedOptionNames,
)
const { getValueComponentProps, loadOptionValues, getValue, getValueType } = useFilterValues()

const filterApplied = (filterId: string, e: FilterItemEmit) => {
  setFilterCondition(filterId, e.selectedCondition)

  const filter = getFilterById(filterId)
  const filterType = getValueType(filter)
  setFilterValue(filterId, filterType, e.value)
}

const addSimilarFilter = async (e: FilterItemEmit, filterOption: FilterOption) => {
  await filterAddingStarted(filterOption)
  const lastFilter = getLastFilter()
  filterApplied(lastFilter.id, e)
}

const filterAddingStarted = async (option: FilterOption) => {
  addEmptyFilter(option)
  await loadOptionValues(option)
}

const debouncedEmit = debounce((val) => {
  emit('update:modelValue', cloneDeep(val))
}, 300)

watch(
  () => props.modelValue,
  () => {
    if (props.modelValue?.length && !isEqual(props.modelValue, validVisibleFilters.value)) {
      const translatedOptions = translateLabels(cloneDeep(props.modelValue))
      setSelectedOptions(translatedOptions)
    }
  },
  { deep: true, immediate: true },
)

watch(validVisibleFilters, (val) => debouncedEmit(val), { deep: true })

watch(
  () => visibleFilters.value,
  (newVal, oldVal) => {
    if (!newVal?.length || oldVal?.length) return

    newVal.filter((row) => row.option.name && row.values).map((row) => loadOptionValues(row.option))
  },
  {
    immediate: true,
  },
)

const helpdeskFilters = computed(() => {
  return props.modelValue.filter(({ option }) => helpdeskGroupFilter(option))
})

const getComponentPropOptionsArray = (componentProps: ValueComponentProps) =>
  componentProps?.groups?.flatMap((item) => item?.options ?? []) ?? []

const remapCustomOptionLabel = (filterOption: AddedFilter): DropdownSelectedValue[] => {
  const filterOptionValue = filterOption.value as DropdownSelectedValue[]
  const optionProps = getComponentPropOptionsArray(filterOption.valueComponentProps)

  return filterOptionValue.map((f) => {
    const match = optionProps.find((o) => o.value === f.value)

    return {
      ...f,
      label: match?.label ?? f.label,
    }
  })
}

const toFilterGroupItem = (d: DeconstructedOption) => {
  const descriptionKey = getDescriptionKey(d.option)
  const hideDescription = d.option.isSpotlight

  const filterOption: AddedFilter = {
    id: d.id,
    label: d.option.label,
    name: d.option.name,
    showAddSimilarButton: !!filterOptions.value.find((o) => o?.name === d.option.name),
    description:
      !hideDescription && translationExists(descriptionKey)
        ? i18n.t(descriptionKey, getDescriptionOptions(d.option))
        : undefined,
    selectedCondition: d.condition,
    conditions: getFilterConditions(d.option),
    value: getValue(d),
    valueType: getValueType(d),
    valueComponentProps: getValueComponentProps(d) as ValueComponentProps,
    intelligenceIcon:
      hasIntelligenceIcon.includes(d.option.name) ||
      d.option.isAutoQa ||
      d.option.isSpotlight ||
      d.option.isCustomCategory,
    botIcon: hasBotIcon(d.option),
    status: d.option.status,
    isSpotlight: d.option.isSpotlight,
    disabled: isDowngradedFilter(d.option) || forcedFilterOption(d.option, props.modalType),
    forceHideRemoveButton: forcedFilterOption(d.option, props.modalType),
  }

  const isGenericCustomCategoryOption = d.option.isCustomCategory && filterOption.valueType === FilterValueType.Generic
  const isDropdownValueArray =
    Array.isArray(filterOption.value) && filterOption.value.every((v) => typeof v === 'object' && 'value' in v)

  if (isGenericCustomCategoryOption && isDropdownValueArray) {
    filterOption.value = remapCustomOptionLabel(filterOption)
  }

  return filterOption
}

const getFilterGroupItems = (filterFn: GroupFilter) => getFilteredItems(filterFn).map(toFilterGroupItem)

const getActiveFilterCount = (groupFilter: GroupFilter) => {
  const groupItems = getFilterGroupItems(groupFilter)
  const activeFilters = groupItems.filter((v) => isActiveFilter(v.value, v.selectedCondition))
  return activeFilters.length
}

const hasActiveVisibleFilters = (filterFn: GroupFilter) => getFilteredItems(filterFn).some((filter) => isActiveFilter(filter.values, filter.condition))

watch(
  () => getActiveFilterCount(dateGroupFilter),
  (count) => {
    emit('disable-selecting-tickets', !!count)
  },
)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("div", _hoisted_3, _toDisplayString(_ctx.$t('filter.groups.date')), 1 /* TEXT */),
        (getActiveFilterCount(_unref(dateGroupFilter)))
          ? (_openBlock(), _createBlock(_unref(UiTag), {
              key: 0,
              rounded: "",
              theme: "brand-light",
              class: "ml-3"
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t('universal.applied', getActiveFilterCount(_unref(dateGroupFilter)))), 1 /* TEXT */)
              ]),
              _: 1 /* STABLE */
            }))
          : _createCommentVNode("v-if", true)
      ])
    ]),
    _createVNode(FilterGroup, {
      "added-filters": getFilterGroupItems(_unref(dateGroupFilter)),
      "filter-options": _unref(filterOptions).filter(_unref(dateGroupFilter)),
      error: !getActiveFilterCount(_unref(dateGroupFilter)),
      onFilterAddingStarted: _cache[0] || (_cache[0] = ($event: any) => (filterAddingStarted($event))),
      onFilterApplied: filterApplied,
      onAddSimilarFilter: addSimilarFilter,
      onRemove: _cache[1] || (_cache[1] = ($event: any) => (_unref(removeFilter)($event)))
    }, null, 8 /* PROPS */, ["added-filters", "filter-options", "error"]),
    (!getActiveFilterCount(_unref(dateGroupFilter)))
      ? (_openBlock(), _createElementBlock("div", _hoisted_4, _toDisplayString(_ctx.$t('filter.date_condition_warning')), 1 /* TEXT */))
      : _createCommentVNode("v-if", true),
    _cache[8] || (_cache[8] = _createElementVNode("div", { class: "bg-gray-20 mb-8 mt-[36px] h-0.5" }, null, -1 /* CACHED */)),
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(groupsWithToggle), (group) => {
      return (_openBlock(), _createElementBlock("div", {
        key: group.name
      }, [
        _createElementVNode("div", {
          class: _normalizeClass(["flex items-center justify-between bg-white pb-3", _ctx.isModal && 'sticky top-0 z-[2]'])
        }, [
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("div", _hoisted_6, _toDisplayString(group.name), 1 /* TEXT */),
            (getActiveFilterCount(_unref(getAllGroupsFilter)(group.subGroups)))
              ? (_openBlock(), _createBlock(_unref(UiTag), {
                  key: 0,
                  rounded: "",
                  theme: "brand-light",
                  class: "ml-3"
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_ctx.$t('universal.applied', getActiveFilterCount(_unref(getAllGroupsFilter)(group.subGroups)))), 1 /* TEXT */)
                  ]),
                  _: 2 /* DYNAMIC */
                }, 1024 /* DYNAMIC_SLOTS */))
              : _createCommentVNode("v-if", true)
          ]),
          _createElementVNode("div", _hoisted_7, [
            (group.showAllFilters.value)
              ? (_openBlock(), _createBlock(SearchInput, {
                  key: 0,
                  modelValue: group.searchValue.value,
                  "onUpdate:modelValue": ($event: any) => ((group.searchValue.value) = $event),
                  small: "",
                  placeholder: _ctx.$t('filter.groups.search_placeholder')
                }, null, 8 /* PROPS */, ["modelValue", "onUpdate:modelValue", "placeholder"]))
              : _createCommentVNode("v-if", true),
            _createVNode(_unref(UiPillToggle), {
              modelValue: group.showAllFilters.value,
              "onUpdate:modelValue": ($event: any) => ((group.showAllFilters.value) = $event)
            }, {
              label: _withCtx(() => [
                _createElementVNode("span", _hoisted_8, _toDisplayString(_ctx.$t('filter.groups.show_all')), 1 /* TEXT */)
              ]),
              _: 2 /* DYNAMIC */
            }, 1032 /* PROPS, DYNAMIC_SLOTS */, ["modelValue", "onUpdate:modelValue"])
          ])
        ], 2 /* CLASS */),
        (group.showAllFilters.value)
          ? (_openBlock(true), _createElementBlock(_Fragment, { key: 0 }, _renderList(group.subGroups, (subGroup, i) => {
              return (_openBlock(), _createElementBlock("div", {
                key: subGroup.name
              }, [
                (
              (group.subGroups.length > 1 && _unref(filterOptions).filter(subGroup.filterFn).length) ||
              getFilterGroupItems(subGroup.filterFn).length
            )
                  ? (_openBlock(), _createElementBlock("div", {
                      key: 0,
                      class: _normalizeClass(["color-gray-70 flex items-center gap-2 text-xxs font-bold uppercase", i > 0 ? 'mt-8' : undefined])
                    }, [
                      _createTextVNode(_toDisplayString(subGroup.name) + " ", 1 /* TEXT */),
                      (subGroup?.tooltipContent)
                        ? (_openBlock(), _createBlock(_unref(Tippy), {
                            key: 0,
                            ref_for: true,
                            ref: "tippyRef",
                            content: subGroup.tooltipContent
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_unref(InfoIcon), { class: "icon-m text-gray-60" })
                            ]),
                            _: 2 /* DYNAMIC */
                          }, 1032 /* PROPS, DYNAMIC_SLOTS */, ["content"]))
                        : _createCommentVNode("v-if", true)
                    ], 2 /* CLASS */))
                  : _createCommentVNode("v-if", true),
                (
              _unref(filterOptions).filter(subGroup.filterFn).length ||
              hasActiveVisibleFilters(subGroup.filterFn) ||
              getFilterGroupItems(subGroup.filterFn).length
            )
                  ? (_openBlock(), _createBlock(FilterGroup, {
                      key: 1,
                      class: "mt-2.5",
                      "added-filters": getFilterGroupItems(subGroup.filterFn),
                      "filter-options": _unref(filterOptions).filter(subGroup.filterFn),
                      "search-filter": group.searchValue.value,
                      "show-all": "",
                      onFilterAddingStarted: _cache[2] || (_cache[2] = ($event: any) => (filterAddingStarted($event))),
                      onFilterApplied: filterApplied,
                      onAddSimilarFilter: addSimilarFilter,
                      onRemove: _cache[3] || (_cache[3] = ($event: any) => (_unref(removeFilter)($event)))
                    }, null, 8 /* PROPS */, ["added-filters", "filter-options", "search-filter"]))
                  : _createCommentVNode("v-if", true)
              ]))
            }), 128 /* KEYED_FRAGMENT */))
          : (_openBlock(), _createBlock(FilterGroup, {
              key: 1,
              "added-filters": getFilterGroupItems(_unref(getAllGroupsFilter)(group.subGroups)),
              "filter-options": _unref(filterOptions).filter(_unref(getAllGroupsFilter)(group.subGroups)),
              onFilterAddingStarted: _cache[4] || (_cache[4] = ($event: any) => (filterAddingStarted($event))),
              onFilterApplied: filterApplied,
              onAddSimilarFilter: addSimilarFilter,
              onRemove: _cache[5] || (_cache[5] = ($event: any) => (_unref(removeFilter)($event)))
            }, null, 8 /* PROPS */, ["added-filters", "filter-options"])),
        _cache[7] || (_cache[7] = _createElementVNode("div", { class: "bg-gray-20 mb-8 mt-[36px] h-0.5" }, null, -1 /* CACHED */))
      ]))
    }), 128 /* KEYED_FRAGMENT */)),
    _createElementVNode("div", _hoisted_9, [
      _createElementVNode("div", _hoisted_10, _toDisplayString(_ctx.$t('filter.groups.helpdesk')), 1 /* TEXT */),
      (getActiveFilterCount(_unref(helpdeskGroupFilter)))
        ? (_openBlock(), _createBlock(_unref(UiTag), {
            key: 0,
            rounded: "",
            theme: "brand-light",
            class: "ml-3"
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('universal.applied', getActiveFilterCount(_unref(helpdeskGroupFilter)))), 1 /* TEXT */)
            ]),
            _: 1 /* STABLE */
          }))
        : _createCommentVNode("v-if", true)
    ]),
    _createVNode(HelpdeskFilterGroup, {
      "added-filters": helpdeskFilters.value,
      "selected-option-names": _unref(selectedOptionNames),
      options: _ctx.options,
      onFilterAddingStarted: _unref(loadOptionValues),
      onFilterApplied: _unref(replaceOrAddFilter),
      onAddSimilarFilter: addSimilarFilter,
      onRemove: _cache[6] || (_cache[6] = ($event: any) => (_unref(removeFilter)($event)))
    }, null, 8 /* PROPS */, ["added-filters", "selected-option-names", "options", "onFilterAddingStarted", "onFilterApplied"])
  ]))
}
}

})